import React, { useState, useEffect } from 'react'
import moment from 'moment-timezone'
import classnames from 'classnames'
import { Spinner } from 'react-bootstrap'
import { Button } from '@abroad/components'

const TimeSlotPicker = ({
  slots = [],
  selectedSlot,
  onSlotSelect,
  timeFormat = '12h',
  onTimeFormatChange,
  isLoading = false,
  timezoneOffsetMinutes = null,
  timezone = null,
  showBackButton = false,
  onBackClick = () => {},
  takenSlots = [],
}) => {
  const [internalTimeFormat, setInternalTimeFormat] = useState(timeFormat)

  useEffect(() => {
    setInternalTimeFormat(timeFormat)
  }, [timeFormat])

  const handleTimeFormatToggle = (format) => {
    setInternalTimeFormat(format)
    if (onTimeFormatChange) {
      onTimeFormatChange(format)
    }
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return ''
    let baseTime
    if (timezone) {
      // When timezone string is provided, use moment-timezone to convert from UTC
      // This automatically handles DST for the specific date
      baseTime = moment.utc(timestamp).tz(timezone)
    } else if (timezoneOffsetMinutes === null) {
      baseTime = moment(timestamp)
    } else {
      // When only timezone offset is provided, convert from UTC to the specified timezone offset
      baseTime = moment.utc(timestamp).utcOffset(timezoneOffsetMinutes)
    }

    return internalTimeFormat === '12h'
      ? baseTime.format('h:mm A')
      : baseTime.format('HH:mm')
  }

  const isSlotSelected = (slot) => {
    if (!selectedSlot || !slot) return false
    return slot.start === selectedSlot.start && slot.end === selectedSlot.end
  }

  if (isLoading) {
    return (
      <div className='time-slot-picker mt-1 pt-3'>
        <div className='time-slot-picker__header'>
          {/* <div className='time-slot-picker__title'>Time slots</div> */}
          <span className='s6'>Time slots</span>
          <div className='time-slot-picker__format-toggle'>
            <button
              type='button'
              className={classnames('time-slot-picker__format-btn s8', {
                'time-slot-picker__format-btn--active':
                  internalTimeFormat === '12h',
              })}
              onClick={() => handleTimeFormatToggle('12h')}>
              12h
            </button>
            <button
              type='button'
              className={classnames('time-slot-picker__format-btn', {
                'time-slot-picker__format-btn--active':
                  internalTimeFormat === '24h',
              })}
              onClick={() => handleTimeFormatToggle('24h')}>
              24h
            </button>
          </div>
        </div>
        <div className='time-slot-picker__loading'>
          <Spinner
            animation='border'
            size='sm'
            className={'font-saffron-700'}
            role='status'
          />
        </div>
      </div>
    )
  }

  if (!slots || slots.length === 0) {
    return (
      <div className='time-slot-picker mt-1 pt-3'>
        <div className='time-slot-picker__header'>
          {/* <div className='time-slot-picker__title'>Time slots</div> */}
          <span className='s6'>Time slots</span>
          <div className='time-slot-picker__format-toggle'>
            <button
              type='button'
              className={classnames('time-slot-picker__format-btn s8', {
                'time-slot-picker__format-btn--active':
                  internalTimeFormat === '12h',
              })}
              onClick={() => handleTimeFormatToggle('12h')}>
              12h
            </button>
            <button
              type='button'
              className={classnames('time-slot-picker__format-btn', {
                'time-slot-picker__format-btn--active':
                  internalTimeFormat === '24h',
              })}
              onClick={() => handleTimeFormatToggle('24h')}>
              24h
            </button>
          </div>
        </div>
        <div className='time-slot-picker__empty'>
          No available time slots for this date
        </div>
      </div>
    )
  }

  return (
    <div className='time-slot-picker mt-1 pt-3'>
      <div className='time-slot-picker__header'>
        {showBackButton && (
          <div className='d-flex align-items-center gap-2 mb-2'>
            <Button
              variant='none'
              className='p-0 border-0 text-saffron-700 hover cursor-pointer'
              onClick={onBackClick}>
              <svg
                width='11'
                height='19'
                viewBox='0 0 11 19'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M9.49615 1.25L1.25 9.25003'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                />
                <path
                  d='M9.49615 17.25L1.25 9.25'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                />
              </svg>
            </Button>
            <span
              className='text-saffron-700 hover cursor-pointer s8'
              onClick={onBackClick}>
              Back to all dates
            </span>
          </div>
        )}
        {!showBackButton && (
          <>
            {/* <div className='time-slot-picker__title'>Time slots</div> */}
            <span className='s6'>Time slots</span>
          </>
        )}
        <div className='time-slot-picker__format-toggle'>
          <button
            type='button'
            className={classnames('time-slot-picker__format-btn s8', {
              'time-slot-picker__format-btn--active':
                internalTimeFormat === '12h',
            })}
            onClick={() => handleTimeFormatToggle('12h')}>
            12h
          </button>
          <button
            type='button'
            className={classnames('time-slot-picker__format-btn', {
              'time-slot-picker__format-btn--active':
                internalTimeFormat === '24h',
            })}
            onClick={() => handleTimeFormatToggle('24h')}>
            24h
          </button>
        </div>
      </div>
      <div className='time-slot-picker__slots mt-20px'>
        {slots.map((slot, index) => {
          const isSelected = isSlotSelected(slot)
          const isTaken =
            Array.isArray(takenSlots) && slot
              ? takenSlots.includes(slot.start)
              : false
          const timeDisplay = formatTime(slot.start)

          return (
            <Button
              key={index}
              variant='abroad-blue'
              size='lg'
              className={classnames('py-2 mb-6px time-slot-picker__slot', {
                'time-slot-picker__slot--selected': isSelected,
                'time-slot-picker__slot--taken': isTaken,
              })}
              onClick={() => !isTaken && onSlotSelect && onSlotSelect(slot)}
              disabled={!slot || isTaken}>
              {timeDisplay}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

export default TimeSlotPicker
